<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { fade } from 'svelte/transition';
    import * as Sentry from '@sentry/browser';
    import { get } from 'svelte/store';
    import { openModal } from 'svelte-modals';
    import { onMount } from 'svelte';

    import { api, clientSettings } from '../../../../stores/api';
    import type { Assignment } from '../Assignment';
    import ColorSymbol from '../../../shared/ColorSymbol.svelte';
    import {
        debug,
        deliveryList,
        jobs,
        leftPageWidthPx,
        planning,
        scenarioManager,
    } from '../../../../stores/store';
    import type { AssignmentJob } from '../AssignmentJob';
    import { getStatus } from '../../../utilities/getStatus';
    import type { PlanningOnMap } from '../PlanningOnMap';
    import JobStatusSegment from '../../Jobs/JobStatusSegment.svelte';
    import { FriendlyDateGenerator } from '../../../utilities/FriendlyDateGenerator';
    import ListElementFriendlyDate from '../../../shared/ListElementFriendlyDate.svelte';
    import type { VehicleResponseType } from '../../Vehicles/types';
    import ModalSelectRoute from '../../Modals/ModalSelectRoute.svelte';
    import './AssignmentJobOne.css';
    import IconPlanningStep3 from '../../../shared/Icons/IconPlanningStep3.svelte';
    import Checkbox from '../../../shared/Checkbox/Checkbox.svelte';
    import { MenuContextCustomActions } from '../../MenuDropdown/MenuContextCustomActions';
    import MenuContextIcon from '../../MenuDropdown/MenuContext/MenuContextIcon.svelte';
    import IconVehicle from '../../../shared/Icons/IconVehicle.svelte';
    import IconRemoveWarning from '../../../shared/Icons/IconRemoveWarning.svelte';
    import ButtonSmallBlueRounded from '../../../shared/ButtonSmallBlueRounded.svelte';
    import type { DisplayModeType, DisplayShapeType } from './types';
    import RoutePointMark from './RoutePointMark.svelte';
    import RoutePointEta from './RoutePointEta.svelte';
    import { type ReorderedRouteType, type RoutePointAlertsType } from '../AssignmentApi';
    import { DeliveriesBulkActions } from './DeliveriesBulkActions';
    import IconPending from '../../../shared/Icons/IconPending.svelte';
    import { rsa } from './RouteSummaryApp/rsa';
    import { dla } from './DeliveryListApp/dla';
    import TimeWindowNotification from '../TimeWindowNotification.svelte';
    import IconRoutePointCompleted from '../../../shared/Icons/IconRoutePointCompleted.svelte';
    import { DeliveriesMenuActions } from './DeliveriesMenuActions';
    import TimeWindowDisplayer from './TimeWindowDisplayer.svelte';
    import { unlockAssignmentIfPossible } from '../unlockAssignmentIfPossible';
    import JobServiceAttributes from '../../Jobs/JobServiceAttributes.svelte';
    import DisplayAddress from './DisplayAddress.svelte';
    import {
        VehicleLimitItem,
        type VehicleLimitExceededType,
    } from '../VehicleLimit/VehicleLimitItem';
    import VehicleLimitPie from '../VehicleLimitPie.svelte';
    import { type JobServiceAttributesType } from '../../Jobs/types';
    import { RoutePointCalc } from './RoutePointCalc';
    import { features } from '../../../shared/Features/features';
    import { setAssignmentJobMenu } from './assignmentjobmenu';

    export let assignmentJob: AssignmentJob;
    export let assignment: Assignment;
    export let showHeader: boolean = false;
    export let unassignedSection: Assignment;
    export let simpleView: boolean = false;
    export let displayMode: DisplayModeType = 'assignmentJob';
    export let displayShape: DisplayShapeType = 'single-middle';
    export let expanded: boolean = false;
    export let canBeExpanded: boolean = false;
    export let modifiedRouteData: ReorderedRouteType | null = null;
    export let routeDirty: boolean = false;
    export let nextRoutePointJobId: number[] = [];
    export let expandedIndex: number[] = []; // jobId
    export let routePointAlerts: RoutePointAlertsType | null = null;
    export let minimizeViewTimeWindow: boolean = false;
    export let disabled: boolean;

    export let onRouteSelect: (
        jobId: number,
        suggestion: boolean,
        assignmentId: number | null,
        newAssignment: Assignment | null | undefined
    ) => Promise<boolean>;
    export let onFilterChange: () => void;
    export let planningOnMap: PlanningOnMap;
    export let vehiclesAssignments: { id: number; vehicle_id: number }[];
    export let vehicles: VehicleResponseType[];
    export let allAssignments: Assignment[] = [];

    let friendlyTargetDate: FriendlyDateGenerator;
    friendlyTargetDate = new FriendlyDateGenerator(
        assignmentJob.getJob()?.target_date ?? '',
        $clientSettings.display.showRelativeToday
    );

    // let isActionReadyToShow: boolean = true;

    let symbolColor: string = '';
    let symbolOpts: any = {};
    let checkboxDisabled = false;
    let checked: boolean = false;

    let assignedTo: Assignment | null = null;
    let previousAssignedTo: {id: number | null, assigned: boolean} = {id: null, assigned: false};
    let assignmentAccepted: boolean = false;
    let assignmentVehicleName: string = '';

    let assignmentJobIsLocked: boolean = false;

    let serviceAttributes: JobServiceAttributesType | null;

    let menu: MenuContextCustomActions = new MenuContextCustomActions();

    let isApproached: boolean = false;
    let isCompleted: boolean = false;
    let isRoutePointCompleted: boolean = false;
    let isRoutePointCurrent: boolean = false;

    let isMounted: boolean = false;

    let vehicleLimits: VehicleLimitExceededType[] = [];

    let routePointEta: number | null = null;

    let lastUsedAssignmentId: number = 0;

    function resetBulkActionSelection() {
        $deliveryList.selectedOrUnselected = [];
        $deliveryList.selectMode = 'selected';
    }

    function updateMenu() {
        if (!isMounted) return;
        if (!assignmentJob) {
            console.error('updateMenu, assignmentJob is null');
            return;
        }

        if ($features.feature_ux_improvements) {

            let actionHandler = function(action: string, end: boolean, aj: AssignmentJob) {
                if (end) {
                    if (['assign', 'assign_suggestion', 'select_another_assignment'].includes(action)) {
                        updateMenu();
                    }
                    if (['select_another_assignment', 'reject', 'assign_to_last_used'].includes(action)) {
                        if (action != 'assign_to_last_used' || assignment.getId() !== assignmentJob.getAssignmentId()) {
                            onFilterChange();
                        }
                    }
                    assignmentJob.setBusy(false);
                }
            }

            assignmentJob = setAssignmentJobMenu(assignmentJob, assignment, actionHandler);
            menu = assignmentJob.getMenu();
            $dla = $dla.updateAssignmentJob(assignmentJob);

        } else {

            // console.log('updateMenu(), MenuContextIcon', $planning.lastUsedAssignment);
            let assignmentJobIsAssigned = DeliveriesMenuActions.assignmentJobIsAssigned(
                assignmentJob,
                assignment
            );
            // (assignment.isDefault() && assignment.getId() === assignmentJob.getAssignmentId()) ||
            // assignmentJob.getAssignmentId() !== 0;
            let assignToActionText = $_('Assign to');
            if (assignmentJobIsAssigned) {
                assignToActionText = $_('Reassign to');
            }

            let rejectActionText = $_('Reject');
            if (assignmentJobIsAssigned && assignment.isDefault()) {
                rejectActionText = $_('Reject from route');
            }

            let select_another_assignment_label = `${assignToActionText} ${$_('another vehicle')}`;
            if (!assignment.isDefault()) {
                select_another_assignment_label = $_('Assign to vehicle');
            }

            //resetBulkActionSelection();

            menu.reset();
            menu.addAction({
                id: 'assign_to_last_used',
                label: `${assignToActionText} <span style="font-width:600;">${
                    $planning.lastUsedAssignment
                        ? $planning.lastUsedAssignment?.getVehicleName()
                        : '[brak pojazdu]'
                }</span> (${$_('last_used')})`,
                disabled: disabled,
                visible:
                    $planning.lastUsedAssignment !== null &&
                    $planning.lastUsedAssignment.getId() !== assignment.getId(),
                icon: IconPlanningStep3,
                visibleInContextMenu: true,
                onClick: () => {
                    unlockAssignmentIfPossible(
                        assignment,
                        () => {
                            if ($planning.lastUsedAssignment !== null) {
                                assignmentJob = assignmentJob.setBusy(true);
                                if (assignment.getId() !== $planning.lastUsedAssignment?.getId()) {
                                    assignmentJob = assignmentJob.setVisibility(false);
                                }
                                onRouteSelect(
                                    assignmentJob.getJobId(),
                                    assignmentJob.hasSuggestedAssignment(),
                                    $planning.lastUsedAssignment?.getId(),
                                    $planning.lastUsedAssignment
                                ).then(async () => {
                                    try {
                                        assignment = await assignment.reload();
                                        assignmentJob = await assignmentJob.loadByJobId(
                                            assignmentJob.getJobId()
                                        );
                                    } catch (e) {
                                        console.error('Cannot reload assignment', e);
                                    }

                                    $dla.getMapModule()?.jobReassign(
                                        assignmentJob.getJobId(),
                                        assignmentJob.getAssignmentId(),
                                        assignmentJob.getId()
                                    );

                                    if (assignment.getId() !== assignmentJob.getAssignmentId()) {
                                        onFilterChange();
                                    }

                                    assignmentJob = assignmentJob.setBusy(false);
                                    if (
                                        !assignment.isDefault() ||
                                        assignment.getId() !== assignmentJob.getAssignmentId()
                                    ) {
                                        $dla.removeItem(assignmentJob.getJobId());
                                        $dla = $dla;
                                    }
                                    updateRouteSummary();
                                });
                            } else {
                                throw new Error('Last used assignment is null (it should not be here)');
                            }
                        },
                        () => {
                            console.log('Assignment is locked, user dont want to unlock');
                        }
                    );
                },
                subMenu: null,
                subMenuIsOpen: false,
            });
            menu.addAction({
                // this action should be only visible for default assignments
                id: 'assign',
                label:
                    assignToActionText +
                    ' <span style="font-width:600;">' +
                    assignment.getVehicleName() +
                    '</span>',
                disabled: disabled,
                visible: DeliveriesMenuActions.deliveryCanBeAssigned(assignmentJob, assignment),
                icon: IconPlanningStep3,
                visibleInContextMenu: true,
                onClick: () => {
                    unlockAssignmentIfPossible(
                        assignment,
                        async () => {
                            $planning.lastUsedAssignment = assignment;
                            assignmentJob = assignmentJob.setBusy(true);
                            let assignedResult = await DeliveriesBulkActions.doAssign(
                                $api,
                                assignment.getId(),
                                [assignmentJob.getJobId()],
                                'selected',
                                {
                                    ...$deliveryList.queryFilter,
                                    ...$deliveryList.query,
                                },
                                $planning.selectedDay
                            ).catch(e => {
                                console.error('doAssign', e);
                            });
                            if (assignedResult?.assigned <= 0) {
                                alert($_('assign_job_to_vehicle_failed'));
                            }
                            assignmentJob = await assignmentJob.loadByJobId(assignmentJob.getJobId());
                            assignmentJob = assignmentJob.setBusy(true);
                            $dla = $dla.updateAssignmentJob(assignmentJob);
                            $dla.getMapModule()?.jobReassign(
                                assignmentJob.getJobId(),
                                assignmentJob.getAssignmentId(),
                                assignmentJob.getId()
                            );

                            $deliveryList.displayedAssignmentJobDetails = $dla
                                .getFilteredItems()
                                .map(aj => {
                                    return {
                                        jobId: aj.getJobId(),
                                        hasAssignment: DeliveriesMenuActions.hasAssignment(aj),
                                        hasSuggestion:
                                            aj.getSuggestedAssignmentId() === assignment.getId(),
                                        hasAdhoc: aj.isAdHoc(),
                                        canBeRejected: DeliveriesMenuActions.deliveryCanBeRejected(
                                            aj,
                                            assignment
                                        ),
                                        canBeAssigned: DeliveriesMenuActions.deliveryCanBeAssigned(
                                            aj,
                                            assignment
                                        ),
                                    };
                                });

                            try {
                                assignment = await assignment.reload();
                            } catch (e) {
                                console.error(e);
                            }
                            assignmentJob = assignmentJob.setBusy(false);
                            updateRouteSummary();
                            updateMenu();
                            resetBulkActionSelection();
                        },
                        () => {
                            console.log('Assignment is locked, user dont want to unlock');
                        }
                    );
                },
                subMenu: null,
                subMenuIsOpen: false,
            });
            menu.addAction({
                // this action should be only visible for default assignments
                id: 'assign_suggestion',
                label: `${assignToActionText} ${assignmentVehicleName || '[brak pojazdu]'} (${$_('best')})`,
                disabled:
                    assignedTo == unassignedSection ||
                    assignment.getId() === assignmentJob.getAssignmentId() ||
                    disabled,
                visible: assignmentVehicleName !== '' && !assignment.isDefault(),
                icon: IconPlanningStep3,
                visibleInContextMenu: true,
                onClick: () => {
                    unlockAssignmentIfPossible(
                        assignment,
                        async () => {
                            console.log('Assignment is locked, but user want to unlock it');
                            // assignSuggestionTo();
                            assignmentJob = assignmentJob.setBusy(true);
                            let assignedResult = await DeliveriesBulkActions.doAssign(
                                $api,
                                assignment.getId(),
                                [assignmentJob.getJobId()],
                                'selected',
                                {
                                    ...$deliveryList.queryFilter,
                                    ...$deliveryList.query,
                                },
                                $planning.selectedDay
                            ).catch(e => {
                                console.error('doAssign', e);
                            });
                            if (assignedResult?.assigned <= 0) {
                                alert($_('assign_job_to_vehicle_failed'));
                            }
                            assignmentJob = await assignmentJob.loadByJobId(assignmentJob.getJobId());
                            assignmentJob = assignmentJob.setBusy(true);
                            $dla = $dla.updateAssignmentJob(assignmentJob);
                            $dla.getMapModule()?.jobReassign(
                                assignmentJob.getJobId(),
                                assignmentJob.getAssignmentId(),
                                assignmentJob.getId()
                            );

                            $deliveryList.displayedAssignmentJobDetails = $dla
                                .getFilteredItems()
                                .map(aj => {
                                    return {
                                        jobId: aj.getJobId(),
                                        hasAssignment: DeliveriesMenuActions.hasAssignment(aj),
                                        hasSuggestion:
                                            aj.getSuggestedAssignmentId() === assignment.getId(),
                                        hasAdhoc: aj.isAdHoc(),
                                        canBeRejected: DeliveriesMenuActions.deliveryCanBeRejected(
                                            aj,
                                            assignment
                                        ),

                                        canBeAssigned: DeliveriesMenuActions.deliveryCanBeAssigned(
                                            aj,
                                            assignment
                                        ),
                                    };
                                });

                            try {
                                assignment = await assignment.reload();
                            } catch (e) {
                                console.error(e);
                            }
                            updateRouteSummary();
                            updateMenu();
                            resetBulkActionSelection();
                            // onFilterChange();
                            assignmentJob = assignmentJob.setBusy(false);
                        },
                        () => {
                            console.log('Assignment is locked, user dont want to unlock');
                        }
                    );
                },
                subMenu: null,
                subMenuIsOpen: false,
            });
            menu.addAction({
                id: 'select_another_assignment',
                // label: `${assignToActionText} ${$_('another vehicle')}...`,
                label: `${select_another_assignment_label}...`,
                disabled: disabled,
                visible:
                    DeliveriesMenuActions.deliveryCanBeRejected(assignmentJob, assignment) ||
                    !assignment.isDefault(),
                icon: IconVehicle,
                visibleInContextMenu: true,
                onClick: () => {
                    unlockAssignmentIfPossible(
                        assignment,
                        () => {
                            console.log('Assignment is locked, but user want to unlock it');
                            selectAnotherAssignment();
                        },
                        () => {
                            console.log('Assignment is locked, user dont want to unlock');
                        }
                    );
                },
                subMenu: null,
                subMenuIsOpen: false,
            });

            menu.addAction({
                id: 'reject',
                label: rejectActionText,
                disabled: disabled,
                visible: DeliveriesMenuActions.deliveryCanBeRejected(assignmentJob, assignment),
                icon: IconRemoveWarning,
                visibleInContextMenu: true,
                onClick: () => {
                    unlockAssignmentIfPossible(
                        assignment,
                        async () => {
                            // console.log('changeAssignments, reject, assId', assignment.getId());
                            assignmentJob = assignmentJob.setBusy(true);
                            assignmentJob = assignmentJob.setVisibility(false);
                            await DeliveriesBulkActions.doReject(
                                get(api),
                                [assignmentJob.getJobId()],
                                'selected',
                                {},
                                assignment.getId(),
                                get(planning).selectedDay
                            ).catch(e => {
                                console.error('doReject', e);
                                assignmentJob = assignmentJob.setVisibility(true);
                            });
                            try {
                                $dla.getMapModule()?.jobReassign(assignmentJob.getJobId(), 0, null);
                                assignment = await assignment.reload();
                                assignmentJob = assignmentJob.setBusy(false);
                                $dla.removeItem(assignmentJob.getJobId());
                            } catch (e) {
                                console.error(e);
                            }
                            $dla = $dla;
                            resetBulkActionSelection();
                            updateRouteSummary();
                            onFilterChange();
                        },
                        () => {
                            console.log('Assignment is locked, user dont want to unlock');
                        }
                    );
                },
                subMenu: null,
                subMenuIsOpen: false,
            });

            menu = menu;
            assignmentJob.setMenu(menu);
            $dla = $dla.updateAssignmentJob(assignmentJob);

        }
    }

    function updateRouteSummary() {
        $rsa = $rsa.startLoading();
        $rsa.reload(assignment)
            .then(ass => {
                assignment = ass;
            })
            .catch(err => {
                console.error('Cannot upload estimate rsa');
            })
            .finally(() => {
                $rsa = $rsa.stopLoading();
            });
    }

    function selectAnotherAssignment() {
        resetBulkActionSelection();
        openModal(ModalSelectRoute, {
            onClick: async (assignmentId, newAssignment) => {
                console.log('ModalSelectRoute onClick async', assignmentId, newAssignment);
                // onRouteSelect 1328557 false 107801 Object { assignment: {…}, vehicle: {…}, maxDeliveriesPerRoute: 200, suggested_jobs: [], quickCategories: {…}, busy: false }
                if (newAssignment) {
                    $planning.lastUsedAssignment = newAssignment;
                    $planning = $planning;
                }
                console.log('ModalSelectRoute onRouteSelect...', assignmentId, newAssignment);
                await onRouteSelect(
                    assignmentJob.getJobId(),
                    assignmentJob.hasSuggestedAssignment(),
                    assignmentId,
                    newAssignment
                );
                console.log('ModalSelectRoute assignment.reload', assignmentId, newAssignment);
                try {
                    assignment = await assignment.reload();
                    assignmentJob = await assignmentJob.loadByJobId(assignmentJob.getJobId());
                } catch (e) {
                    console.error(e);
                }
                updateRouteSummary();
                updateMenu();
                resetBulkActionSelection();
                $dla = $dla.updateAssignmentJob(assignmentJob);
                if (
                    !assignment.isDefault() ||
                    assignment.getId() !== assignmentJob.getAssignmentId()
                ) {
                    console.log(
                        '_reload, selectAnotherAssignment',
                        assignment.getId(),
                        assignmentJob.getAssignmentId()
                    );
                    $dla.removeItem(assignmentJob.getJobId());
                    $dla = $dla;
                    onFilterChange();
                }
                console.log(
                    '_reload, selectAnotherAssignment, ModalSelectRoute onClick return exit',
                    assignment.getId(),
                    assignmentJob.getAssignmentId()
                );
                return Promise.resolve(true);
            },
            rejectedAssignments: assignmentJob.getJobClass()?.getRejectedAssignments(),
            selectedScenarioId: assignment.getScenarioId(),
            vehicles: $scenarioManager.getVehiclesData(),
            jobId: assignmentJob.getJobId(),
            inProgressSwitch: false,
            onlyLocked: false,
        });
    }

    onMount(() => {
        isMounted = true;
        updateMenu();
    });

    $: {
        let td = assignmentJob.getJob()?.target_date;
        if (typeof td === 'string') {
            friendlyTargetDate.set(td);
        } else {
            friendlyTargetDate.set('');
        }
        friendlyTargetDate = friendlyTargetDate;
    }

    $: {
        try {
            assignedTo = unassignedSection;
            assignmentAccepted = true;
            if (
                assignmentJob.getAssignmentId() > 0 ||
                (assignmentJob.getSuggestedAssignmentId() || 0) > 0
            ) {
                if (
                    assignment.getId() > 0 && // FIXME: assignment.isDefault
                    ((assignmentJob.getAssignmentId() &&
                        assignment.getId() === assignmentJob.getAssignmentId()) ||
                        assignment.getId() === assignmentJob.getSuggestedAssignmentId())
                ) {
                    assignedTo = assignment;
                    assignmentAccepted =
                        assignmentJob.getAssignmentId() > 0 &&
                        assignment.getId() === assignmentJob.getAssignmentId();
                } else if (
                    assignmentJob.getAssignmentId() &&
                    allAssignments.filter(a => assignmentJob.getAssignmentId() == a.getId())[0]
                ) {
                    assignedTo = allAssignments.filter(
                        a => assignmentJob.getAssignmentId() == a.getId()
                    )[0];
                    assignmentAccepted = true;
                } else if (
                    allAssignments.filter(
                        a => assignmentJob.getSuggestedAssignmentId() == a.getId()
                    )[0]
                ) {
                    assignedTo = allAssignments.filter(
                        a => assignmentJob.getSuggestedAssignmentId() == a.getId()
                    )[0];
                    assignmentAccepted = false;
                }
            }
            /*if (
                assignedTo &&
                !assignmentAccepted &&
                (((assignedTo.isLocked() || assignedTo.isRouteModifiedAfterDownload()) &&
                    !assignmentJob.getJob()?.ad_hoc) ||
                    (assignedTo.hasRouteBeenEverDownloaded() &&
                        assignmentJob.getJob()?.special_attributes?.['delivery_or_collection'] !==
                            'collection'))
            ) {
                assignedTo = unassignedSection;
            }*/
            if (assignedTo) {
                if (assignedTo.getId() !== previousAssignedTo.id || assignmentAccepted !== previousAssignedTo.assigned) {
                    previousAssignedTo = {id: assignedTo.getId(), assigned: assignmentAccepted};
                    let va = vehiclesAssignments.find(va => va.id === assignedTo?.getId());
                    assignmentVehicleName = va
                        ? (vehicles.find(v => v.id === va?.vehicle_id)?.name ?? '')
                        : '';
                    symbolColor = assignedTo.getColor();
                    symbolOpts = assignedTo.getPinOpts(!assignmentAccepted);
                    updateMenu();
                } else {
                    assignmentJob = assignmentJob;
                }
            } else {
                Sentry.captureMessage('Assigned to not found', {
                    extra: {
                        assignmentJob: assignmentJob,
                        allAssignments: allAssignments,
                        assignment: assignment,
                        unassignedSection: unassignedSection,
                    },
                });
            }
        } catch (e) {
            console.error('symbolColor', e);
        }
        //if (assignmentJob.getJobId() == 111331) console.log('!!! 111331', assignmentJob.getAssignmentId());
    }

    $: {
        checkboxDisabled = !DeliveriesMenuActions.deliveryCanBeChecked(assignmentJob, assignment);
        checked =
            !checkboxDisabled &&
            (($deliveryList.selectMode === 'filtered' &&
                !$deliveryList.selectedOrUnselected.includes(assignmentJob.getJobId())) ||
                ($deliveryList.selectMode === 'selected' &&
                    $deliveryList.selectedOrUnselected.includes(assignmentJob.getJobId())));
    }

    $: {
        isApproached = DeliveriesMenuActions.isRoutePointApproached(assignmentJob);
        isCompleted = typeof assignmentJob.getServiceAttributes()?.delivery === 'number';
        isRoutePointCompleted = DeliveriesMenuActions.isRoutePointCompleted(assignmentJob);
        isRoutePointCurrent = DeliveriesMenuActions.isRoutePointCurrent(assignmentJob);
        assignmentJobIsLocked = DeliveriesMenuActions.assignmentJobIsLocked(
            assignmentJob,
            assignment
        );
    }

    $: {
        if (displayMode === 'routePoint' && routePointAlerts) {
            vehicleLimits = [];
            let limit_dict: any = { parcels: 'parcel_limit', total_weight: 'weight_limit' };
            for (let type in routePointAlerts) {
                if (!routePointAlerts[type].fulfilled) {
                    let limitItem = VehicleLimitItem.getVehicleLimit(
                        limit_dict[type],
                        routePointAlerts[type].value,
                        routePointAlerts[type].limit
                    );
                    if (limitItem) {
                        vehicleLimits.push(limitItem);
                    }
                }
            }
        }
    }

    $: {
        if (displayMode === 'assignmentJob' && assignmentJob.getJob()?.rejected) {
            try {
                serviceAttributes = $dla
                    .getRejectedJobsAsAssignmentJobs(false, [assignmentJob.getJobId()])
                    [assignmentJob.getJobId()].getServiceAttributes();
            } catch (e) {
                serviceAttributes = null;
                console.warn('Cannot get service attributes for rejected job');
            }
        } else {
            serviceAttributes = assignmentJob.getServiceAttributes();
        }
    }

    $: {
        routePointEta = RoutePointCalc.getEta(
            modifiedRouteData,
            assignmentJob.getRoutePoint(),
            assignment
        );
    }

    $: {
        if ($planning.lastUsedAssignment && ($planning.lastUsedAssignment?.getId() || 0) != lastUsedAssignmentId) {
            lastUsedAssignmentId = $planning.lastUsedAssignment.getId();
            updateMenu();
        }
    }

    $: disabled = disabled;
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="list-element delivery-list-element-checkbox list-element-data"
    class:minimizeViewTimeWindow
    class:delivery-list-element-assignmentJob={displayMode === 'assignmentJob'}
    class:delivery-list-element-routePoint={displayMode === 'routePoint'}
    class:simple-view={simpleView}
    class:not-visible={!assignmentJob.isVisible()}
    class:expandedIndex={expandedIndex.includes(assignmentJob.getJobId())}
    style="position: static"
    on:click={evt => {
        let job = assignmentJob.getJob();
        console.log('Clicked element on list, job: ', job);
        // select when no selected, unselect when selected
        $deliveryList.expandedIndex = $deliveryList.expandedIndex.includes(assignmentJob.getJobId())
            ? []
            : [assignmentJob.getJobId()];
        console.log('$deliveryList.expandedIndex', $deliveryList.expandedIndex);
        if (job && job.geom) {
            Targeo.Base.Map.zoomAndCenter(
                { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                19
            );
            try {
                planningOnMap.jobShowBalloon(job.id);
            } catch (e) {
                console.warn('Cannot show balloon', job.id);
            }
        }
    }}
>
    <div class="checkbox">
        {#if !assignmentJob.isBusy()}
            {#if displayMode === 'assignmentJob'}
                {#if DeliveriesMenuActions.deliveryCanBeChecked(assignmentJob, assignment)}
                    <Checkbox
                        label=""
                        {checked}
                        disabled={checkboxDisabled || disabled}
                        onChange={() => {
                            if (
                                $deliveryList.selectedOrUnselected.includes(
                                    assignmentJob.getJobId()
                                )
                            ) {
                                $deliveryList.selectedOrUnselected =
                                    $deliveryList.selectedOrUnselected.filter(
                                        id => id !== assignmentJob.getJobId()
                                    );
                            } else {
                                $deliveryList.selectedOrUnselected = [
                                    ...$deliveryList.selectedOrUnselected,
                                    assignmentJob.getJobId(),
                                ];
                            }
                        }}
                    />
                {:else}
                    <div></div>
                {/if}
            {/if}
            {#if displayMode === 'routePoint'}
                <div
                    on:click|stopPropagation={() => {
                        expanded = canBeExpanded ? !expanded : false;
                        console.log('expanded', expanded);
                    }}
                >
                    <RoutePointMark
                        {displayShape}
                        value={assignmentJob.getRoutePoint()?.point_order.toString() ?? ''}
                        {isApproached}
                        isCompleted={isRoutePointCompleted}
                        isCurrent={isRoutePointCurrent}
                        isNotConfirmed={!DeliveriesMenuActions.hasAssignment(assignmentJob)}
                    />
                </div>
            {/if}
        {:else}
            <IconPending spin={true} scale={0.7} />
        {/if}
    </div>

    <div class="color">
        {#if displayMode === 'routePoint' && assignmentJob.getRoutePoint()}
            <div style="display: flex; justify-content: flex-end; align-items: center;">
                <div style="width:50px;">
                    <RoutePointEta
                        timeWindow={assignment.getTimeWindow(assignmentJob)}
                        routePoint={assignmentJob.getRoutePoint()}
                        {assignment}
                        {modifiedRouteData}
                        {routeDirty}
                    />
                </div>
                <div style="width:21px;">
                    {#if routePointEta !== null && !routeDirty}
                        <TimeWindowNotification
                            timeWindow={assignment.getTimeWindow(assignmentJob)}
                            scale={0.7}
                        />
                    {/if}
                </div>
            </div>
        {/if}
        {#if displayMode === 'assignmentJob'}
            {#if isCompleted}
                <IconRoutePointCompleted />
            {:else}
                <div style="width:23px;transform: translate(-5px,0);">
                    <ColorSymbol
                        color={symbolColor}
                        opts={symbolOpts}
                        type={assignmentJob.getJobType()}
                        scale={simpleView ? 0.6 : 0.9}
                    />
                </div>
            {/if}
        {/if}
    </div>
    <div
        class="address-2"
        class:row={simpleView}
        style="display: flex; flex-direction: row; align-items: center; justify-content: flex-start;"
    >
        {#if $debug}
            #{assignmentJob.getJobId()}
        {/if}
        <DisplayAddress
            {assignmentJob}
            flexColumn={$leftPageWidthPx < 800 || $features.feature_configurable_job_description}
            nextAddressOnRoute={nextRoutePointJobId.includes(assignmentJob.getJobId())}
        />
    </div>

    <div
        class="date-2"
        class:date-important={assignmentJob.getJob()?.target_date === $planning.selectedDay}
        data-target-date={assignmentJob.getJob()?.target_date}
        data-today={$planning.selectedDay}
    >
        <ListElementFriendlyDate
            disabled={!assignmentJob?.getJobClass()?.editable() ?? false}
            {friendlyTargetDate}
            {simpleView}
            isAdHoc={assignmentJob.isAdHoc()}
            jobId={assignmentJob.getJobId()}
        />
    </div>

    <div class="status-2">
        <JobStatusSegment
            jobType={assignmentJob.getJobType()}
            jobStatus={getStatus(assignmentJob.getJob())}
            isNewlyAdded={assignmentJob.isNewlyAdded()}
            isOptional={assignmentJob.isOptional($planning.selectedDay)}
            isOverdue={assignmentJob.isOverdue()}
            timeWindow={assignment.getRouteInfo()?.alerts?.time_window[
                assignmentJob.getJobId() || 0
            ]}
            jobId={assignmentJob.getJobId()}
            jobTargetDate={assignmentJob.getTargetDate()}
            adHoc={assignmentJob.isAdHoc()}
            adHocClickable={$debug}
            {simpleView}
            showAdhocIfExists={$debug}
        />
        {#if displayMode === 'routePoint'}
            {#if routePointAlerts && !routeDirty}
                <VehicleLimitPie alerts={routePointAlerts} />
            {/if}
        {/if}
        {#if serviceAttributes || $debug}
            <div style="padding-left: 5px; display: flex; align-items: center;">
                <JobServiceAttributes
                    jobType={assignmentJob.getJobType()}
                    {serviceAttributes}
                    scale={0.6}
                />
            </div>
        {/if}
    </div>

    <div class="time-window">
        <TimeWindowDisplayer
            timeWindow={assignment.getTimeWindow(assignmentJob)}
            minimizeView={minimizeViewTimeWindow}
        />
    </div>

    <div
        class="other dont-break"
        class:column-hidden={assignmentJobIsLocked || isApproached || $debug}
        in:fade={{ delay: 160, duration: 160 }}
    >
        {#if displayMode === 'assignmentJob' || true}
            {#if !assignmentJob.isBusy() && !assignmentJobIsLocked && !isApproached}
                {#if menu.isAvailable('assign')}
                    <MenuContextIcon
                        {menu}
                        displayMode="text"
                        disabled={disabled || !menu.hasVisibleItems()}
                    />
                    <ButtonSmallBlueRounded
                        {disabled}
                        text={$_('Assign')}
                        margin="0 3px"
                        on:click={evt => {
                            evt.stopPropagation();
                            menu.triggerAction('assign');
                        }}
                    />
                {:else if menu.isAvailable('assign_suggestion')}
                    <MenuContextIcon
                        {menu}
                        displayMode="text"
                        disabled={disabled || !menu.hasVisibleItems()}
                    />
                    <ButtonSmallBlueRounded
                        {disabled}
                        text={$_('Assign') + '...'}
                        margin="0 3px"
                        on:click={evt => {
                            evt.stopPropagation();
                            menu.triggerAction('assign_suggestion');
                        }}
                    />
                {:else}
                    <MenuContextIcon
                        {menu}
                        displayMode="button"
                        disabled={disabled || !menu.hasVisibleItems()}
                    />
                {/if}
            {/if}
        {/if}
    </div>
</div>

<style>
    .not-visible {
        display: none;
    }
    .expandedIndex {
        background-color: #e5f1fd;
    }
    .column-hidden {
        display: block;
    }
</style>
